/**
 * Skills Types
 *
 * Type definitions for workspace skills.
 * Skills are specialized instructions that extend Claude's capabilities.
 */

/**
 * Skill metadata from SKILL.md YAML frontmatter
 */
export interface SkillMetadata {
  /** Display name for the skill */
  name: string;
  /** Brief description shown in skill list */
  description: string;
  /** Optional alternate identifiers that can invoke this skill */
  aliases?: string[];
  /** Optional file patterns that trigger this skill */
  globs?: string[];
  /** Optional tools to always allow when skill is active */
  alwaysAllow?: string[];
  /**
   * Optional icon - emoji or URL only.
   * - Emoji: rendered directly in UI (e.g., "🔧")
   * - URL: auto-downloaded to icon.{ext} file
   * Note: Relative paths and inline SVG are NOT supported.
   */
  icon?: string;
  /** Optional source slugs to auto-enable when this skill is invoked */
  requiredSources?: string[];
}

/** Source of a loaded skill */
export type SkillSource = 'default' | 'global' | 'workspace' | 'project';

/**
 * Plugin name for project-level and global skills.
 *
 * The SDK derives plugin names from `path.basename()` of the registered plugin
 * directory. Both `{project}/.agents/` and `~/.agents/` share the basename
 * `.agents`, so skills from either tier resolve to `.agents:skillSlug`.
 */
export const AGENTS_PLUGIN_NAME = '.agents';

/**
 * A loaded skill with parsed content
 */
export interface LoadedSkill {
  /** Directory name (slug) */
  slug: string;
  /** Parsed metadata from YAML frontmatter */
  metadata: SkillMetadata;
  /** Full SKILL.md content (without frontmatter) */
  content: string;
  /** Absolute path to icon file if exists */
  iconPath?: string;
  /** Absolute path to skill directory */
  path: string;
  /** Where this skill was loaded from */
  source: SkillSource;
  /** Whether this is a built-in default skill (cannot be deleted) */
  isDefault?: boolean;
}

export type SkillIdentifierCarrier = Pick<LoadedSkill, 'slug' | 'metadata'>;

export function getSkillIdentifiers(skill: SkillIdentifierCarrier): string[] {
  const aliases = skill.metadata.aliases ?? [];
  return Array.from(new Set(
    [skill.slug, ...aliases]
      .map(identifier => identifier.trim())
      .filter(Boolean)
  ));
}

export function getAllSkillIdentifiers(skills: SkillIdentifierCarrier[]): string[] {
  return Array.from(new Set(skills.flatMap(getSkillIdentifiers)));
}

export function resolveSkillIdentifier<T extends SkillIdentifierCarrier>(
  skills: T[],
  identifier: string
): T | undefined {
  const normalized = identifier.trim();
  return skills.find(skill => getSkillIdentifiers(skill).includes(normalized));
}
